import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Eye, EyeOff } from 'lucide-react';
import { checkLogin, change_MyData, setOtpVerified } from '../../../Common/store';
import { getOneHourExpiry } from '../../../Common/utils/sessionExpiry';
import { adminLogin } from '../../api/adminAuth';
import { useToast } from '../../../Common/context/ToastContext';
import { useTooltip } from '../../../Common/context/TooltipContext';
import { getLoginBackgroundAssetUrl, getLoginLogoAssetUrl, getLoginLogoStyle } from '../../../Common/constants/adminImages';
import PhpPayLoginShell from '../../../Common/components/Login/PhpPayLoginShell';
import { BRAND_COPYRIGHT, LOGIN_LAYOUT, SERVICE_BRAND_NAME, brandPageAttributes } from '../../../Common/config/brandConfig';

const SESSION_EXPIRY_KEY = 'session_expiry';

/**
 * 관리자 로그인 - POST /api/admin/login 연동
 * requireOtp 시 OTP 페이지로, 아니면 대시보드로 이동
 */
function AdminLogin() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { resetShowAll } = useTooltip();
  const [id, setId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    resetShowAll();
  }, [resetShowAll]);

  // 로그인 페이지에서 스크롤바 제거
  useEffect(() => {
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const adminId = id.trim();
    if (!adminId || !password) {
      showToast('아이디와 비밀번호를 입력해 주세요.');
      return;
    }
    setLoading(true);
    try {
      const data = await adminLogin(adminId, password);
      const { user, requireOtp, screen } = data ?? {};
      dispatch(checkLogin(true));
      dispatch(change_MyData({
        ...user,
        name: '관리자',
        type: 'admin',
      }));
      if (requireOtp) {
        dispatch(setOtpVerified(false));
        navigate('/admin/otp');
      } else {
        dispatch(setOtpVerified(true));
        const expiry = String(getOneHourExpiry());
        try {
          sessionStorage.setItem(SESSION_EXPIRY_KEY, expiry);
          localStorage.setItem('ls_session_expiry', expiry);
        } catch (err) {}
        navigate('/admin/settlement');
      }
    } catch (err) {
      showToast(err.message || '로그인에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  if (LOGIN_LAYOUT === 'phpay-split') {
    return (
      <PhpPayLoginShell
        logoSrc={getLoginLogoAssetUrl()}
        logoStyle={{ width: 'auto', height: '42px' }}
        portalLabel="ADMIN PORTAL"
        title="관리자 로그인"
        description="phpay 서비스 운영을 위한 관리자 계정으로 로그인해 주세요."
      >
        <form onSubmit={handleSubmit} className="phpay-login-form">
          <div className="phpay-login-field">
            <label htmlFor="phpay-admin-id">아이디</label>
            <input
              id="phpay-admin-id"
              type="text"
              value={id}
              onChange={(e) => setId(e.target.value)}
              placeholder="아이디 또는 휴대폰번호"
              autoComplete="username"
            />
          </div>
          <div className="phpay-login-field">
            <label htmlFor="phpay-admin-password">비밀번호</label>
            <div className="phpay-login-input-wrap">
              <input
                id="phpay-admin-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="비밀번호"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="phpay-login-password-toggle"
                aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
              >
                {showPassword ? <EyeOff size={19} /> : <Eye size={19} />}
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="phpay-login-primary-button"
          >
            {loading ? '로그인 중...' : '로그인'}
          </button>
        </form>
      </PhpPayLoginShell>
    );
  }

  return (
    <div {...brandPageAttributes} className="fixed inset-0 flex justify-center items-center z-10 overflow-hidden bg-[#fff]">
      {/* 배경 이미지 - 1920px */}
      <div
        className="absolute top-0 bottom-0 bg-cover bg-center bg-no-repeat"
        style={{
          width: 'min(100vw, 1920px)',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundImage: `url('/img/login_bg_img.png')`,
          // backgroundImage: `url(${getLoginBackgroundAssetUrl()})`,
        }}
      />

      {/* 좌측 상단 텍스트 */}

      {/* 중앙 로그인 카드 */}
      <div className="relative z-10 w-[500px] bg-white rounded-2xl shadow-xl p-10 mx-4">
        <div className="flex justify-center">
          <div className="rounded-xl flex items-center justify-center">
            <img src={getLoginLogoAssetUrl()} alt={SERVICE_BRAND_NAME} className="h-12 object-contain" style={getLoginLogoStyle({ marginBottom: '10px' })} />
          </div>
        </div>
        <div className="text-white text-[14px] font-medium z-20 mb-8" style={{ color: '#000', textAlign: 'center' }}>
          관리자페이지
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-[14px] font-bold text-[#191f28] mb-2">아이디</label>
            <input
              type="text"
              value={id}
              onChange={(e) => setId(e.target.value)}
              placeholder="아이디 또는 휴대폰번호"
              className="w-full px-4 py-3 rounded-lg border border-[#e5e8eb] bg-white text-[15px] text-[#191f28] placeholder:text-[#adb5bd] focus:border-[var(--brand-primary)] focus:outline-none transition-colors"
            />
          </div>
          <div>
            <label className="block text-[14px] font-bold text-[#191f28] mb-2">비밀번호</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="비밀번호"
                className="w-full px-4 py-3 pr-12 rounded-lg border border-[#e5e8eb] bg-white text-[15px] text-[#191f28] placeholder:text-[#adb5bd] focus:border-[var(--brand-primary)] focus:outline-none transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8b95a1] hover:text-[#191f28] p-1 transition-colors"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 text-white rounded-lg font-bold text-[16px] transition-colors hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#191f28' }}
          >
            {loading ? '로그인 중...' : '로그인'}
          </button>
        </form>
        <div
          style={{
            position: 'absolute',
            bottom: '-40px',
            left: '50%',
            transform: 'translateX(-50%)',
            textAlign: 'center',
            width: '100%',
          }}
        >
          <p style={{ fontSize: '12px', color: '#000', margin: 0 }}>
            {BRAND_COPYRIGHT}
          </p>
        </div>

        {/* <div className="mt-8 pt-6 border-t border-[#f2f4f6] text-center">
          <Link to="/cp" className="text-[14px] font-medium text-[#8b95a1] hover:text-[var(--brand-primary)] transition-colors">
            가맹점 로그인
          </Link>
        </div> */}
      </div>

      {/* 하단 로고 */}
      {/* <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
        <img src="/img/ntoss_logo_black.png" alt="ntoss" className="h-8 object-contain" />
      </div> */}
    </div>
  );
}

export default AdminLogin;
